import type {
  ChatMessageContent,
  ChatSelectedTemplateItem,
} from "schemas/generated/chatMessage";

import React from "react";

import { PageTemplateCardSkeleton } from "@editor/components/shops-ui/home/PageTemplateCard";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import PreviewThumbnail from "@/features/funnel/components/PreviewThumbnail";
import Button from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { useIsChatLoading } from "../store/ChatStoreContext";

type Props = {
  toolResult: Extract<
    ChatMessageContent[number],
    { type: "tool-result" }
  > | null;
  toolMessageId?: string;
};

function SuggestedTemplates({ toolResult, toolMessageId }: Props) {
  if (!toolResult) {
    return <div>Fetching templates...</div>;
  }

  if (!toolResult.result.suggestedTemplateIds) {
    return <div>No templates found</div>;
  }

  const selectedTemplateId = toolResult.result.selectedTemplateId;

  if (selectedTemplateId) {
    return <SelectedTemplate templateId={selectedTemplateId} />;
  }

  return (
    <div className="flex flex-row gap-2 overflow-x-auto w-full min-w-0">
      {toolResult.result.suggestedTemplateIds.map((templateId) => (
        <SuggestedTemplate
          key={templateId}
          templateId={templateId}
          toolResult={toolResult}
          toolMessageId={toolMessageId}
        />
      ))}
    </div>
  );
}

export default SuggestedTemplates;

function SuggestedTemplate({
  templateId,
  toolResult,
  toolMessageId,
}: {
  templateId: string;
  toolResult: Extract<
    ChatMessageContent[number],
    { type: "tool-result" }
  > | null;
  toolMessageId?: string;
}) {
  const projectId = useCurrentProjectId();
  const { openModal } = useModal();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const isChatLoading = useIsChatLoading();
  const { data: template, isLoading } = trpc.fileTemplate.get.useQuery(
    projectId
      ? {
          projectId,
          id: templateId,
        }
      : skipToken,
  );

  const { mutate: updateMessage } = trpc.chat.updateMessage.useMutation({
    onSuccess: () => {
      void trpcUtils.chat.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void trpcUtils.chat.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const { mutate: addMessage } = trpc.chat.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  const handleThumbnailClick = () => {
    if (!template) {
      return;
    }

    openModal({
      type: "pageTemplatePreviewModal",
      props: {
        template,
        chatId: chatId ?? undefined,
        onSuccess: (fileId) => {
          if (projectId && chatId && toolResult && toolMessageId) {
            updateMessage({
              id: toolMessageId,
              projectId,
              content: [
                {
                  type: "tool-result",
                  toolCallId: toolResult.toolCallId,
                  toolName: toolResult.toolName,
                  result: {
                    ...toolResult?.result,
                    selectedTemplateId: templateId,
                  },
                },
              ],
            });

            const selectedTemplateItem: ChatSelectedTemplateItem = {
              type: "selectedTemplate",
              selectedTemplateId: templateId,
              selectedTemplateName: template.name,
              fileId,
            };

            addMessage({
              projectId,
              chatId,
              content: [
                {
                  type: "text",
                  contentType: "selectedTemplate" as const,
                  text: JSON.stringify(selectedTemplateItem),
                },
              ],
              role: "user",
            });
          }
        },
      },
    });
  };

  if (isLoading) {
    return <PageTemplateCardSkeleton />;
  }

  return (
    <div className="flex flex-col h-full w-full min-w-[275px] border-0.5 border-border rounded-lg p-3">
      <div className="relative group">
        <PreviewThumbnail
          pageRoute={`/template-preview/${template?.id}`}
          className={twMerge(
            "w-full h-[300px] border-b-0.5 border-border rounded-lg transition-all duration-150 shadow-md",
          )}
        />

        <div className="absolute inset-0 rounded-lg rounded-b-none pointer-events-none transition-colors duration-150 group-hover:bg-white/20" />

        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <Button
            disabled={isChatLoading}
            isLoading={isChatLoading}
            variant="primary"
            onClick={handleThumbnailClick}
          >
            Use Template
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-0.5 pt-3">
        <h4 className="header-h4 line-clamp-1">{template?.name}</h4>
      </div>
    </div>
  );
}

function SelectedTemplate({ templateId }: { templateId: string }) {
  const projectId = useCurrentProjectId();
  const { data: template, isLoading } = trpc.fileTemplate.get.useQuery(
    projectId
      ? {
          projectId,
          id: templateId,
        }
      : skipToken,
  );
  if (isLoading) {
    return <PageTemplateCardSkeleton />;
  }

  return (
    <div className="flex flex-col h-full border-0.5 border-border rounded-lg w-[200px]">
      <div className="relative group">
        <PreviewThumbnail
          pageRoute={`/template-preview/${template?.id}`}
          className={twMerge(
            "w-full h-[200px] cursor-pointer border-b-0.5 border-border rounded-t-lg rounded-b-none",
          )}
        />
      </div>
      <div className="flex flex-col gap-0.5 py-3 px-4">
        <h4 className="header-h4 line-clamp-1">Selected {template?.name}</h4>
      </div>
    </div>
  );
}
